import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { MessageSquare, Check, X, CheckCircle2, Trash2, Pencil, CornerDownRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { useProjectRole } from '@/features/auth/context/project-role-context'
import {
  addFieldComment,
  resolveComment,
  updateComment,
  deleteComment,
  addReplyComment,
  type CommentRow,
} from '../api/records-api'
import { cn } from '@/lib/utils/cn'
import dayjs from 'dayjs'

interface FieldCommentThreadProps {
  recordId: string
  submissionId: string
  fieldKey: string
  /** All field comments for this submission, pre-fetched by parent */
  allComments: CommentRow[]
  onResolve?: () => void
}

function isWithin5Min(createdAt: string) {
  return dayjs().diff(dayjs(createdAt), 'minute') < 5
}

export function FieldCommentThread({
  recordId,
  submissionId,
  fieldKey,
  allComments,
  onResolve,
}: FieldCommentThreadProps) {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const { role } = useProjectRole()
  const { toast } = useToast()
  const onCommentError = (label: string) => (err: Error) =>
    toast({ title: label, description: err.message, variant: 'destructive' })
  const [open, setOpen] = useState(false)
  const [body, setBody] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')
  const [replyingToId, setReplyingToId] = useState<string | null>(null)
  const [replyBody, setReplyBody] = useState('')

  const fieldComments = allComments.filter((c) => c.field_key === fieldKey && !c.parent_comment_id)
  const replyMap = allComments.reduce<Record<string, CommentRow[]>>((acc, c) => {
    if (c.parent_comment_id) {
      acc[c.parent_comment_id] = [...(acc[c.parent_comment_id] ?? []), c]
    }
    return acc
  }, {})

  const openDeclineCount = allComments.filter(
    (c) => c.field_key === fieldKey && c.origin === 'decline' && !c.is_resolved
  ).length
  const totalCount = allComments.filter((c) => c.field_key === fieldKey).length

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['field-comments', recordId] })
    qc.invalidateQueries({ queryKey: ['record-comments', recordId] })
    qc.invalidateQueries({ queryKey: ['decline-comments', recordId] })
  }

  const addMutation = useMutation({
    mutationFn: () => addFieldComment({ recordId, submissionId, fieldKey, body, userId: user!.id, userRole: role ?? 'viewer' }),
    onSuccess: () => { invalidate(); setBody('') },
    onError: onCommentError('خطا در ثبت نظر'),
  })

  const resolveMutation = useMutation({
    mutationFn: (commentId: string) => resolveComment(commentId, user!.id),
    onSuccess: () => { invalidate(); onResolve?.() },
    onError: onCommentError('خطا در حل نظر'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, newBody }: { id: string; newBody: string }) => updateComment(id, newBody),
    onSuccess: () => { invalidate(); setEditingId(null); setEditBody('') },
    onError: onCommentError('خطا در ویرایش نظر'),
  })

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => deleteComment(commentId, user!.id),
    onSuccess: () => invalidate(),
    onError: onCommentError('خطا در حذف نظر'),
  })

  const replyMutation = useMutation({
    mutationFn: (parentId: string) =>
      addReplyComment({ recordId, parentCommentId: parentId, body: replyBody, userId: user!.id, userRole: role ?? 'viewer', submissionId, fieldKey, scope: 'field' }),
    onSuccess: () => { invalidate(); setReplyingToId(null); setReplyBody('') },
    onError: onCommentError('خطا در ارسال پاسخ'),
  })

  const renderComment = (c: CommentRow, isReply = false) => {
    const isOwn = c.author_id === user?.id
    const canEdit = isOwn && isWithin5Min(c.created_at)
    const replies = replyMap[c.id] ?? []

    return (
      <div key={c.id}>
        <div
          className={cn(
            'px-3 py-2.5',
            isReply && 'ps-6 border-s-2 border-muted',
            c.origin === 'decline' && !c.is_resolved ? 'bg-red-500/5 border-s-2 border-red-500' : 'bg-card'
          )}
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-[9px] font-medium text-primary shrink-0">
                {(c.author?.full_name ?? 'U')[0]}
              </div>
              <span className="text-[11px] font-medium text-foreground">{c.author?.full_name ?? 'کاربر'}</span>
              {c.origin === 'decline' && (
                <span className="text-[9px] text-red-400 bg-red-500/10 px-1 py-0.5 rounded">اصلاحی</span>
              )}
              {c.is_resolved && (
                <span className="text-[9px] text-emerald-400 bg-emerald-500/10 px-1 py-0.5 rounded flex items-center gap-0.5">
                  <CheckCircle2 className="w-2.5 h-2.5" />حل شده
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-muted-foreground">{dayjs(c.created_at).fromNow()}</span>
              {c.origin === 'decline' && !c.is_resolved && (
                <button type="button" className="text-[10px] text-emerald-400 hover:text-emerald-300" onClick={() => resolveMutation.mutate(c.id)} disabled={resolveMutation.isPending}>
                  حل شد
                </button>
              )}
              {canEdit && (
                <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => { setEditingId(c.id); setEditBody(c.body) }}>
                  <Pencil className="w-3 h-3" />
                </button>
              )}
              {isOwn && (
                <button type="button" className="text-muted-foreground hover:text-red-400" onClick={() => deleteMutation.mutate(c.id)} disabled={deleteMutation.isPending}>
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
              {!isReply && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => setReplyingToId(replyingToId === c.id ? null : c.id)}
                  aria-label="پاسخ به نظر"
                  title="پاسخ به نظر"
                >
                  <CornerDownRight className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {editingId === c.id ? (
            <div className="space-y-1.5 mt-1">
              <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={2} className="text-xs resize-none" autoFocus />
              <div className="flex gap-1 justify-end">
                <Button type="button" size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => setEditingId(null)}>
                  <X className="w-3 h-3 me-1" />انصراف
                </Button>
                <Button type="button" size="sm" className="h-6 text-xs px-2" disabled={!editBody.trim() || updateMutation.isPending} onClick={() => updateMutation.mutate({ id: c.id, newBody: editBody })}>
                  <Check className="w-3 h-3 me-1" />ذخیره
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-foreground leading-relaxed">{c.body}</p>
          )}
        </div>

        {/* Replies */}
        {replies.map((r) => renderComment(r, true))}

        {/* Reply compose */}
        {replyingToId === c.id && (
          <div className="ps-6 px-3 py-2 bg-muted/10 border-s-2 border-muted space-y-1.5">
            <Textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder="پاسخ..." rows={2} className="text-xs resize-none" autoFocus />
            <div className="flex gap-1 justify-end">
              <Button type="button" size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => { setReplyingToId(null); setReplyBody('') }}>
                <X className="w-3 h-3 me-1" />انصراف
              </Button>
              <Button type="button" size="sm" className="h-6 text-xs px-2" disabled={!replyBody.trim() || replyMutation.isPending} onClick={() => replyMutation.mutate(c.id)}>
                <Check className="w-3 h-3 me-1" />ارسال
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="mt-2">
      <button
        type="button"
        className={cn(
          'flex items-center gap-1.5 text-[11px] transition-opacity',
          openDeclineCount > 0 ? 'text-red-400 hover:text-red-300' : 'text-muted-foreground hover:text-foreground',
          // Quiet by default: only reveal on field hover / focus. Stays visible
          // whenever a thread already exists, so existing comments are never
          // hidden.
          totalCount > 0
            ? 'opacity-100'
            : 'opacity-0 group-hover/field:opacity-100 focus-visible:opacity-100',
        )}
        onClick={() => setOpen(true)}
      >
        <MessageSquare className="w-3 h-3" />
        <span>
          {totalCount > 0 ? (
            <>
              {totalCount.toLocaleString('fa-IR')} نظر
              {openDeclineCount > 0 && <span className="ms-1 text-red-400">({openDeclineCount.toLocaleString('fa-IR')} اصلاحی)</span>}
            </>
          ) : (
            'افزودن نظر'
          )}
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-md p-0 gap-0 flex flex-col max-h-[80vh]">
          <DialogHeader className="px-4 py-3 border-b border-border text-start sm:text-start">
            <DialogTitle className="text-sm flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              نظرات فیلد
              {openDeclineCount > 0 && (
                <span className="text-[10px] font-normal text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">
                  {openDeclineCount.toLocaleString('fa-IR')} اصلاحی
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-border">
            {fieldComments.length > 0 ? (
              fieldComments.map((c) => renderComment(c))
            ) : (
              <p className="px-4 py-8 text-center text-xs text-muted-foreground">نظری ثبت نشده است</p>
            )}
          </div>

          {/* Composer pinned to the bottom of the modal */}
          <div className="px-4 py-3 bg-muted/20 border-t border-border space-y-2">
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="نظر خود را بنویسید..." rows={2} className="text-sm resize-none" />
            <div className="flex justify-end">
              <Button type="button" size="sm" className="h-7 text-xs px-3" disabled={!body.trim() || addMutation.isPending} onClick={() => addMutation.mutate()}>
                <Check className="w-3 h-3 me-1" />ثبت نظر
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
